/**
 * Module Management API
 *
 * RTK Query endpoints for module operations:
 * - List available modules
 * - Get enabled modules
 * - Enable/disable modules
 * - Get audit logs
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
// IDN-17: Sidebar/ModuleRoute/useModuleEnabled all gate on the
// `enabledModules` Redux slice, which is populated only by a one-shot
// fetchEnabledModules thunk dispatched once at App mount -- invalidating
// this (moduleApi) slice's own RTK Query cache tags does nothing for that
// separate slice. Sync it directly on a successful enable/disable instead
// of requiring an app reload.
import { addModule, removeModule } from '../../../app/slices/enabledModulesSlice';

export const moduleApiPaths = {
  availableModules: '/api/v1/auth/modules/',
  tenantModules: '/api/v1/auth/tenant/modules/',
  enableModule: (moduleKey) => `/api/v1/auth/tenant/modules/${moduleKey}/enable/`,
  disableModule: (moduleKey) => `/api/v1/auth/tenant/modules/${moduleKey}/disable/`,
  moduleAuditLogs: '/api/v1/auth/tenant/modules/audit-logs/',
  moduleAuditLogsByKey: (moduleKey) => `/api/v1/auth/tenant/modules/${moduleKey}/audit-logs/`,
  moduleConfig: (moduleKey) => `/api/v1/auth/tenant/modules/${moduleKey}/config/`,
};

export const moduleApi = createApi({
  reducerPath: 'moduleApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['TenantModules', 'ModuleAuditLogs'],
  endpoints: (builder) => ({
    /**
     * Get all available modules for current tenant's subscription tier
     */
    getAvailableModules: builder.query({
      query: () => ({ url: moduleApiPaths.availableModules }),
      transformResponse: (response) => response.modules || [],
      providesTags: ['TenantModules'],
    }),

    /**
     * Get currently enabled modules for tenant
     */
    getTenantModules: builder.query({
      query: () => ({ url: moduleApiPaths.tenantModules }),
      transformResponse: (response) => response.modules || [],
      providesTags: ['TenantModules'],
    }),

    /**
     * Enable a module for the tenant
     */
    enableModule: builder.mutation({
      query: ({ moduleKey, reason = '' }) => ({
        url: moduleApiPaths.enableModule(moduleKey),
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['TenantModules', 'ModuleAuditLogs'],
      // IDN-17: keep enabledModulesSlice (Sidebar/ModuleRoute/useModuleEnabled)
      // in sync immediately rather than waiting for the next App mount.
      async onQueryStarted({ moduleKey }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(addModule(moduleKey));
        } catch {
          // Enable failed -- nothing to sync.
        }
      },
    }),

    /**
     * Disable a module for the tenant
     */
    disableModule: builder.mutation({
      query: ({ moduleKey, reason = '', force = false }) => ({
        url: moduleApiPaths.disableModule(moduleKey),
        method: 'POST',
        body: { reason, force },
      }),
      invalidatesTags: ['TenantModules', 'ModuleAuditLogs'],
      // IDN-17: see enableModule above.
      async onQueryStarted({ moduleKey }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(removeModule(moduleKey));
        } catch {
          // Disable failed -- nothing to sync.
        }
      },
    }),

    /**
     * Get audit logs for module changes
     */
    getModuleAuditLogs: builder.query({
      query: (moduleKey) => ({
        url: moduleKey
          ? moduleApiPaths.moduleAuditLogsByKey(moduleKey)
          : moduleApiPaths.moduleAuditLogs,
      }),
      transformResponse: (response) => response.logs || [],
      providesTags: ['ModuleAuditLogs'],
    }),

    /**
     * Update module configuration settings
     */
    updateModuleConfig: builder.mutation({
      query: ({ moduleKey, config }) => ({
        url: moduleApiPaths.moduleConfig(moduleKey),
        method: 'PATCH',
        body: { config },
      }),
      invalidatesTags: ['TenantModules'],
    }),
  }),
});

export const {
  useGetAvailableModulesQuery,
  useGetTenantModulesQuery,
  useEnableModuleMutation,
  useDisableModuleMutation,
  useGetModuleAuditLogsQuery,
  useUpdateModuleConfigMutation,
} = moduleApi;
